<template>
	<div class="pick-action">
		<div class="pa-head">
			<span class="pa-count" role="status">
				<b class="vn-num">{{ fa(readyCount) }}</b> ردیف آماده<template v-if="draftCount"> · <b class="vn-num">{{ fa(draftCount) }}</b> ردیف ناقص</template>
			</span>
			<div class="pa-tools">
				<button type="button" class="pa-link" @click="openGuide"><VnIcon name="search" />پیدا کردن کد مشتری</button>
				<button type="button" class="pa-link danger" :disabled="!hasContent" @click="clearAll"><VnIcon name="trash" />حذف همه</button>
			</div>
		</div>

		<div v-if="rejectedNote" class="pa-rejected" role="alert">{{ rejectedNote }}</div>

		<ol class="pa-rows">
			<li class="pa-labels" aria-hidden="true"><span /><span>شماره موبایل نیروی اجرایی</span><span>کد مشتری</span><span /></li>
			<li v-for="(row, index) in rows" :key="row.key" class="pa-row" :class="{ rejected: row.rejected, partial: isPartial(row) }">
				<span class="pa-index vn-num">{{ fa(index + 1) }}</span>
				<div class="pa-cell">
					<input :ref="'phone' + index" v-model="row.promoter_phone_number" inputmode="numeric" dir="ltr" maxlength="11" autocomplete="off"
						:aria-label="`شماره موبایل نیروی اجرایی، ردیف ${index + 1}`" :aria-invalid="phoneInvalid(row) ? 'true' : 'false'" placeholder="09123456789"
						@input="row.rejected = false" @keydown.enter.prevent="focusNext(index, 'code')" />
					<small v-if="phoneInvalid(row)" class="pa-error">شماره باید ۱۱ رقم و با ۰۹ شروع شود.</small>
				</div>
				<div class="pa-cell">
					<input :ref="'code' + index" v-model="row.outlet_code" dir="ltr" autocomplete="off" :aria-label="`کد مشتری، ردیف ${index + 1}`" placeholder="1001"
						@input="row.rejected = false" @keydown.enter.prevent="focusNext(index, 'add')" />
					<small v-if="row.rejected" class="pa-error">شماره یا کد در این پروژه پیدا نشد.</small>
				</div>
				<button type="button" class="pa-del" :title="`حذف ردیف ${index + 1}`" :aria-label="`حذف ردیف ${index + 1}`" @click="removeRow(index)"><VnIcon name="trash" /></button>
			</li>
		</ol>

		<div class="pa-add">
			<button type="button" class="pa-add-btn" @click="addRow(true)"><VnIcon name="plus" />افزودن ردیف</button>
			<span class="pa-hint">با Enter در آخرین ردیف، ردیف تازه باز می‌شود.</span>
		</div>

		<div class="pa-upload" :class="{ over: dragging, busy: uploading }" @dragover.prevent="dragging = true" @dragleave.prevent="dragging = false" @drop.prevent="onDrop">
			<VnIcon name="download" class="pa-upload-icon" />
			<div class="pa-upload-copy">
				<b>{{ uploading ? 'در حال خواندن فایل…' : 'یا فایل اکسل را بارگذاری کنید' }}</b>
				<small>فایل را اینجا بکشید یا <label class="pa-pick" tabindex="0" @keydown.enter.prevent="$refs.file.click()">انتخاب کنید<input ref="file" type="file" accept=".xlsx,.csv" @change="onFile" /></label>
					· <a :href="sampleUrl" download>دریافت فایل نمونه</a></small>
				<small v-if="fileError" class="pa-error" role="alert">{{ fileError }}</small>
			</div>
		</div>

		<Modal ref="guide" size="large" close-btn title="پیدا کردن کد مشتری">
			<template #mnx-header><h3 class="pa-modal-title">پیدا کردن کد مشتری</h3></template>
			<template #mnx-body>
				<div class="pa-guide-filters">
					<label>استان
						<select v-model="pickProvince" class="form-select" @change="onProvince">
							<option :value="null" disabled>انتخاب کنید</option>
							<option v-for="province in provinces" :key="province.id" :value="province.id">{{ province.name }}</option>
						</select>
					</label>
					<label>شهر
						<select v-model="pickCity" class="form-select" :disabled="!pickProvince">
							<option :value="null">همه‌ی شهرها</option>
							<option v-for="item in cities" :key="item.city.id" :value="item.city.id">{{ item.city.name }}</option>
						</select>
					</label>
					<label>نوع مشتری
						<select v-model="pickCategory" class="form-select">
							<option :value="null">همه</option>
							<option v-for="item in categories" :key="item.id" :value="item.id">{{ item.verbose_name || item.name }}</option>
						</select>
					</label>
					<button type="button" class="pa-search" :disabled="!pickProvince || searching" @click="searchOutlets">{{ searching ? 'در حال جست‌وجو…' : 'جست‌وجو' }}</button>
				</div>
				<p v-if="searchError" class="pa-error" role="alert">{{ searchError }}</p>
				<p v-else-if="outlets !== null && !outlets.length" class="pa-empty">مشتری‌ای با این فیلترها پیدا نشد.</p>
				<ul v-if="outlets && outlets.length" class="pa-outlets">
					<li v-for="outlet in outlets" :key="outlet.id">
						<button type="button" @click="useOutlet(outlet)">
							<span class="pa-o-main"><b>{{ outlet.name }}</b><small>{{ outlet.city && outlet.city.name }} · {{ categoryName(outlet) }}<template v-if="outlet.address"> · {{ outlet.address }}</template></small></span>
							<span class="pa-o-code vn-mono">{{ outlet.code }}</span>
						</button>
					</li>
				</ul>
				<p v-if="outlets && outlets.length" class="pa-hint">روی یک مشتری کلیک کنید تا کد او در نخستین ردیف خالی قرار بگیرد.</p>
			</template>
		</Modal>
	</div>
</template>

<script>
import Modal from '../Modal/Modal.vue';
import VnIcon from '../VnIcon/index.vue';

const DIGITS = { '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4', '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9', '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4', '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9' };
const latin = value => String(value == null ? '' : value).replace(/[۰-۹٠-٩]/g, d => DIGITS[d]).trim();
const PHONE = /^09\d{9}$/;
let keyCounter = 0;
const blank = () => ({ key: (keyCounter += 1), promoter_phone_number: '', outlet_code: '', rejected: false });

// Rows of "field staff phone + customer code". The parent receives only the complete, valid rows
// through `sendPhoneNumber`; incomplete rows stay on screen and are never submitted.
export default {
	name: 'PickAction',
	components: { Modal, VnIcon },
	data() {
		return {
			rows: [blank()], dragging: false, uploading: false, fileError: '', rejectedNote: '',
			provinces: [], cities: [], categories: [], pickProvince: null, pickCity: null, pickCategory: null,
			outlets: null, searching: false, searchError: '',
			sampleUrl: 'https://s3.ir-thr-at1.arvanstorage.ir/leadv2-bucket/Action_Plan_Import.xlsx',
		};
	},
	computed: {
		readyRows() {
			return this.rows
				.map(row => ({ promoter_phone_number: latin(row.promoter_phone_number), outlet_code: latin(row.outlet_code) }))
				.filter(row => PHONE.test(row.promoter_phone_number) && row.outlet_code);
		},
		readyCount() { return this.readyRows.length; },
		draftCount() { return this.rows.filter(row => this.isPartial(row)).length; },
		hasContent() { return this.rows.some(row => row.promoter_phone_number || row.outlet_code); },
	},
	watch: {
		readyRows: { immediate: true, handler(value) { this.$emit('sendPhoneNumber', value); } },
	},
	methods: {
		fa(value) { return Number(value).toLocaleString('fa-IR'); },
		isPartial(row) {
			const phone = latin(row.promoter_phone_number);
			const code = latin(row.outlet_code);
			return !!(phone || code) && !(PHONE.test(phone) && code);
		},
		phoneInvalid(row) {
			const phone = latin(row.promoter_phone_number);
			return phone.length >= 11 && !PHONE.test(phone);
		},
		addRow(focus = false) {
			this.rows.push(blank());
			if (focus) this.$nextTick(() => this.focus(this.rows.length - 1, 'phone'));
		},
		focus(index, kind) {
			const el = this.$refs[kind + index];
			if (el && el[0]) el[0].focus();
		},
		focusNext(index, target) {
			if (target === 'code') { this.focus(index, 'code'); return; }
			if (index === this.rows.length - 1) this.addRow(true);
			else this.focus(index + 1, 'phone');
		},
		removeRow(index) {
			if (this.rows.length === 1) this.rows = [blank()];
			else this.rows.splice(index, 1);
		},
		clearAll() {
			if (this.readyCount > 1 && !window.confirm('همه‌ی ردیف‌ها پاک شود؟')) return;
			this.rows = [blank()];
			this.rejectedNote = '';
		},
		// after a submit: keep only the rows the server could not match, so they can be corrected
		keepRejected(errors) {
			const failed = new Set((errors || []).map(item => `${latin(item.promoter_phone_number)}|${latin(item.outlet_code)}`));
			const kept = this.rows.filter(row => failed.has(`${latin(row.promoter_phone_number)}|${latin(row.outlet_code)}`));
			kept.forEach((row) => { row.rejected = true; });
			this.rows = kept.length ? kept : [blank()];
			this.rejectedNote = kept.length
				? `${this.fa(kept.length)} ردیف ثبت نشد؛ شماره‌ی نیروی اجرایی یا کد مشتری در این پروژه پیدا نشد. آن‌ها را اصلاح کنید و دوباره ثبت کنید.`
				: '';
		},
		reset() { this.rows = [blank()]; this.rejectedNote = ''; this.fileError = ''; },

		// ── Excel ──
		onDrop(event) {
			this.dragging = false;
			const [file] = event.dataTransfer.files || [];
			if (file) this.upload(file);
		},
		onFile(event) {
			const [file] = event.target.files || [];
			event.target.value = ''; // the same file can be chosen again after a fix
			if (file) this.upload(file);
		},
		async upload(file) {
			this.fileError = '';
			if (!/\.(xlsx|csv)$/i.test(file.name)) { this.fileError = 'فقط فایل xlsx یا csv پذیرفته می‌شود.'; return; }
			this.uploading = true;
			const body = new FormData();
			body.append('file', file);
			const res = await this.$ApiServiceLayer.post(this.$PATH.RELATIVE_PATH.POST.UPLOAD_EXCEL_FILE + '?p=' + this.$STORE.state.userConfig.setProjectId, '', body);
			this.uploading = false;
			const data = res.status === 200 && res.data && Array.isArray(res.data.data) ? res.data.data : null;
			if (!data) { this.fileError = 'فایل خوانده نشد. از فایل نمونه استفاده کنید و سرستون‌ها را تغییر ندهید.'; return; }
			const added = data.filter(item => item && (item.promoter_phone_number || item.outlet_code))
				.map(item => ({ ...blank(), promoter_phone_number: latin(item.promoter_phone_number), outlet_code: latin(item.outlet_code) }));
			if (!added.length) { this.fileError = 'فایل ردیفی نداشت.'; return; }
			const existing = this.rows.filter(row => row.promoter_phone_number || row.outlet_code);
			this.rows = [...existing, ...added];
			this.rejectedNote = '';
			this.$notify({ group: 'tc', type: 'success', text: `${this.fa(added.length)} ردیف از فایل اضافه شد.` });
		},

		// ── customer code finder ──
		async openGuide() {
			this.$refs.guide.open();
			if (!this.provinces.length) {
				const project = this.$STORE.state.userConfig.setProjectId;
				const [provinces, categories] = await Promise.all([
					this.$ApiServiceLayer.get(this.$PATH.RELATIVE_PATH.GET.GET_PROVINCE_LIST + '?p=' + project, this.$PATH.SERVICE_NAME.AUTH),
					this.$ApiServiceLayer.get(this.$PATH.RELATIVE_PATH.GET.OUTLET_CAT + '?p=' + project, this.$PATH.SERVICE_NAME.AUTH),
				]);
				if (provinces.status === 200) this.provinces = provinces.data;
				if (categories.status === 200) this.categories = categories.data;
			}
		},
		async onProvince() {
			this.pickCity = null; this.cities = []; this.outlets = null;
			const res = await this.$ApiServiceLayer.get(
				`${this.$PATH.RELATIVE_PATH.GET.GET_CITY_LIST}?city__province=${this.pickProvince}&p=${this.$STORE.state.userConfig.setProjectId}`,
				this.$PATH.SERVICE_NAME.AUTH,
			);
			if (res.status === 200) this.cities = res.data;
		},
		async searchOutlets() {
			this.searching = true; this.searchError = '';
			const query = [`p=${this.$STORE.state.userConfig.setProjectId}`, `city__province=${this.pickProvince}`,
				this.pickCity ? `city=${this.pickCity}` : '', this.pickCategory ? `category=${this.pickCategory}` : ''].filter(Boolean).join('&');
			const res = await this.$ApiServiceLayer.get(`${this.$PATH.RELATIVE_PATH.GET.GET_OUTLET_LIST}?${query}`, this.$PATH.SERVICE_NAME.AUTH);
			this.searching = false;
			if (res.status !== 200) { this.searchError = this.$ApiServiceLayer.getErrorMessage(res); return; }
			this.outlets = Array.isArray(res.data) ? res.data : (res.data.results || []);
		},
		categoryName(outlet) {
			const names = { ChainStore: 'زنجیره‌ای', MultiBrand: 'مولتی برند', BrandShop: 'برندشاپ' };
			return outlet.category ? (names[outlet.category.name] || outlet.category.verbose_name || outlet.category.name) : '';
		},
		useOutlet(outlet) {
			let target = this.rows.find(row => !latin(row.outlet_code));
			if (!target) { target = blank(); this.rows.push(target); }
			target.outlet_code = String(outlet.code);
			target.rejected = false;
			this.$notify({ group: 'tc', type: 'success', text: `کد ${outlet.code} در فهرست قرار گرفت.` });
		},
	},
};
</script>

<style scoped>
.pick-action { display: grid; gap: 14px; }
.pa-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px 16px; }
.pa-count { font-size: 13px; color: var(--text-2); }
.pa-count b { color: var(--navy-900); font-size: 15px; }
.pa-tools { display: flex; flex-wrap: wrap; gap: 6px 18px; }
.pa-link { display: inline-flex; align-items: center; gap: 6px; padding: 0; border: 0; background: transparent; font: inherit; font-size: 12.5px; font-weight: 800; color: var(--link); cursor: pointer; }
.pa-link .vn-icon { width: 15px; height: 15px; }
.pa-link.danger { color: var(--pink-700); }
.pa-link:disabled { color: var(--text-4); cursor: default; }
.pa-rejected { padding: 10px 14px; border-radius: var(--r-sm); background: var(--pink-50); color: var(--pink-700); font-size: 12.5px; font-weight: 700; line-height: 1.9; }
.pa-rows { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.pa-labels, .pa-row { display: grid; grid-template-columns: 34px minmax(0, 1fr) minmax(0, 1fr) 40px; gap: 10px; align-items: start; }
.pa-labels { font-size: 12px; font-weight: 800; color: var(--text-3); }
.pa-index { height: 44px; display: grid; place-items: center; border-radius: var(--r-xs); background: var(--navy-900); color: var(--amber-500); font-size: 12px; font-weight: 800; }
.pa-cell { display: grid; gap: 4px; min-width: 0; }
#app .pick-action .pa-cell input { width: 100%; height: 44px; padding: 0 12px; border: 1px solid var(--line-strong); border-radius: var(--r-xs); background: var(--surface); font-family: var(--font-num); font-size: 14px; color: var(--text); box-shadow: none; text-align: left; }
#app .pick-action .pa-cell input::placeholder { color: var(--text-4); }
#app .pick-action .pa-cell input:focus { outline: none; border-color: var(--navy-900); box-shadow: 2px 2px 0 var(--teal-500); }
#app .pick-action .pa-cell input[aria-invalid='true'], #app .pick-action .pa-row.rejected .pa-cell input { border-color: var(--pink-500); background: var(--pink-50); }
.pa-error { color: var(--pink-700); font-size: 11.5px; font-weight: 700; }
.pa-del { width: 40px; height: 44px; display: grid; place-items: center; border: 1px solid var(--line-strong); border-radius: var(--r-xs); background: var(--surface); color: var(--text-3); cursor: pointer; }
.pa-del:hover { border-color: var(--pink-500); background: var(--pink-50); color: var(--pink-700); }
.pa-del .vn-icon { width: 17px; height: 17px; }
.pa-add { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 14px; }
.pa-add-btn { display: inline-flex; align-items: center; gap: 6px; min-height: 40px; padding: 0 16px; border: 1px solid var(--navy-900); border-radius: var(--r-xs); background: var(--surface); font: inherit; font-size: 13px; font-weight: 800; color: var(--navy-900); cursor: pointer; }
.pa-add-btn:hover { background: var(--navy-900); color: #fff; }
.pa-add-btn .vn-icon { width: 16px; height: 16px; }
.pa-hint { font-size: 11.5px; color: var(--text-3); }
.pa-outlets + .pa-hint { display: block; margin: 10px 0 0; }
.pa-upload { display: flex; align-items: center; gap: 14px; padding: 18px; border: 2px dashed var(--line-strong); border-radius: var(--r-sm); background: var(--surface-sunken); transition: border-color .15s, background .15s; }
.pa-upload.over { border-color: var(--teal-700); background: var(--teal-50); }
.pa-upload.busy { opacity: .6; }
.pa-upload-icon { flex: none; width: 34px; height: 34px; padding: 7px; border-radius: var(--r-xs); background: var(--navy-900); color: #fff; --icon-accent: var(--teal-500); transform: rotate(180deg); }
.pa-upload-copy { display: grid; gap: 3px; }
.pa-upload-copy b { font-size: 13px; color: var(--text); }
.pa-upload-copy small { font-size: 12px; color: var(--text-3); }
.pa-pick { display: inline; margin: 0; font-size: 12px; font-weight: 800; color: var(--link); text-decoration: underline; cursor: pointer; }
.pa-pick input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.pa-pick:focus-visible { outline: 3px solid var(--focus); outline-offset: 2px; }
.pa-modal-title { margin: 0; font-size: 16px; font-weight: 900; color: var(--navy-900); }
.pa-guide-filters { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)) auto; gap: 12px; align-items: end; }
.pa-guide-filters label { display: grid; gap: 4px; margin: 0; font-size: 12px; font-weight: 800; color: var(--text-2); }
.pa-search { min-height: 40px; padding: 0 18px; border: 2px solid var(--navy-900); border-radius: var(--r-xs); background: var(--navy-900); color: #fff; font: inherit; font-size: 13px; font-weight: 900; cursor: pointer; }
.pa-search:disabled { opacity: .45; cursor: default; }
.pa-empty { margin: 14px 0 0; text-align: center; font-size: 13px; color: var(--text-3); }
.pa-outlets { list-style: none; margin: 14px 0 0; padding: 0; display: grid; gap: 6px; max-height: 340px; overflow-y: auto; }
.pa-outlets button { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; padding: 10px 12px; border: 1px solid var(--line); border-radius: var(--r-sm); background: var(--surface); text-align: right; font: inherit; cursor: pointer; }
.pa-outlets button:hover { border-color: var(--navy-900); box-shadow: 3px 3px 0 var(--teal-500); }
.pa-o-main { display: grid; min-width: 0; }
.pa-o-main b { font-size: 13px; color: var(--text); }
.pa-o-main small { font-size: 11.5px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pa-o-code { flex: none; padding: 3px 10px; border-radius: var(--r-xs); background: var(--amber-500); color: var(--navy-900); font-size: 13px; font-weight: 800; }
@media (max-width: 640px) {
	.pa-labels { display: none; }
	.pa-row { grid-template-columns: 30px minmax(0, 1fr) 40px; }
	.pa-row .pa-cell:nth-of-type(2) { grid-column: 2; grid-row: 2; }
	.pa-row .pa-del { grid-row: 1 / span 2; grid-column: 3; }
	.pa-index { grid-row: 1 / span 2; }
	.pa-guide-filters { grid-template-columns: 1fr; }
}
</style>
